import React, { useCallback, useMemo, useState } from "react";
import ContasLiberacaoVendedorDefaultForm from "./ContasLiberacaoVendedorDefaultForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getComplementoOperacao,
  isEnviandoComplementoOperacao,
  isGravandoComplementoOperacao,
} from "../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../hooks/useCompleteForm";
import { actions } from "../../../../actions/operacao.actions";
import validators from "../../../../utils/validators";

const ContasLiberacaoVendedorDefaultFormConnected = ({
  handleGoTo0,
  vendedores,
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: { tipoConta: tiposConta, banco: bancos },
  } = useSelector(getComplementoOperacao);
  const submitting = useSelector(isGravandoComplementoOperacao);
  const submittingDocumentos = useSelector(isEnviandoComplementoOperacao);
  const [habilitados, setHabilitados] = useState([]);

  const contasArray = useMemo(() => {
    const vendedoresComConjuges = vendedores.reduce((a, v) => {
      a.push(v);
      if (
        v?.conjuge?.conjugeVendedor &&
        !vendedores.some((vendedor) => vendedor.cpfCnpj === v.conjuge.cpfCnpj)
      ) {
        a.push({ ...v.conjuge, id: v.id });
      }
      return a;
    }, []);

    return vendedoresComConjuges.map((v, i) => {
      if (v.contaLiberacao) {
        setHabilitados((prev) => [...prev, i]);
        return {
          ...v.contaLiberacao,
          index: i,
          habilitado: true,
          nome: v.nome,
          conjuge: v?.conjugeVendedor ? true : false,
          participante: v.id,
        };
      }

      return {
        index: i,
        nome: v.nome,
        conjuge: v?.conjugeVendedor ? true : false,
        participante: v.id,
        habilitado: false,
      };
    });
  }, [vendedores]);

  const requiredIfActive = useCallback(
    (value, index) => {
      if (habilitados.includes(index)) {
        if (value === undefined || value === null || value === "") {
          return "Campo obrigatório";
        }
      }
      return true;
    },
    [habilitados]
  );

  const validatePercentualLiberacao = useCallback(
    (values, index) => {
      if (!habilitados.includes(index)) {
        return true;
      }
      return values.contas
        .filter((c) => c.habilitado)
        .reduce((acc, v) => acc + v.percentualLiberacao, 0) !== 100
        ? "A soma dos percentuais deve ser igual a 100%"
        : true;
    },
    [habilitados]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      contas: contasArray.map((conta) => {
        return {
          bancoConta: validators.object({
            custom: {
              default: (value) => requiredIfActive(value, conta.index),
            },
          }),
          agenciaConta: validators.string({
            custom: {
              default: (value) => requiredIfActive(value, conta.index),
            },
          }),
          numeroConta: validators.string({
            custom: {
              default: (value) => requiredIfActive(value, conta.index),
            },
          }),
          tipoConta: validators.object({
            custom: {
              default: (value) => requiredIfActive(value, conta.index),
            },
          }),
          percentualLiberacao: validators.number({
            custom: {
              default: (value, values) =>
                validatePercentualLiberacao(values, conta.index),
            },
          }),
        };
      }),
    }),
    initialValues: useCallback(
      () => ({
        contas: contasArray,
      }),
      [contasArray]
    ),
    handleSubmit: useCallback(
      (values) => {
        const data = {
          id: operacao.id,
          participantes: values.contas.map((v) => {
            return {
              id: v.participante,
              contaLiberacao: v.habilitado && !!v.numeroConta
                ? {
                    id: v.id,
                    idBancoConta: v.bancoConta?.id,
                    agenciaConta: v.agenciaConta,
                    numeroConta: v.numeroConta,
                    idTipoConta: v.tipoConta?.id,
                    percentualLiberacao: v.percentualLiberacao,
                    conjuge: v.conjuge,
                  }
                : {
                  id: v.id,
                  conjuge: v.conjuge,
                },
            };
          }),
        };

        dispatch(
          actions.gravarComplemento.request({
            callback: () => handleGoTo0(),
            instituicao: operacao.codigoInstituicao,
            passo: "contas-liberacao",
            operacao: data
          })
        );
      },
      [dispatch, operacao.codigoInstituicao, operacao.id, handleGoTo0]
    ),
  });

  const toggleHabilitado = (index) => {
    if (habilitados.includes(index)) {
      setHabilitados((prev) => prev.filter((i) => i !== index));
      return;
    }
    setHabilitados((prev) => [...prev, index]);
  };

  const contas = formProps.watch("contas");

  return (
    <ContasLiberacaoVendedorDefaultForm
      formProps={formProps}
      handleSubmit={handleSubmit}
      submitting={submitting || submittingDocumentos}
      contas={contas}
      bancos={bancos}
      tiposConta={tiposConta}
      handleGoBack={handleGoTo0}
      toggleHabilitado={toggleHabilitado}
    />
  );
};

export default ContasLiberacaoVendedorDefaultFormConnected;
